import styled, { css, keyframes } from 'styled-components';

import { AFFILIATE_FORM, ANIM, CASINO_SETTINGS, COLORS, REDUCED_MOTION } from '../../../../theme/tokens';
import { Text } from '../../../../components';
import { DRAG_DIM_OPACITY } from '@/nova/components/Sheet/Sheet.styles';

export const HiddenInput = styled.input`
  display: none;
`;

// Everything except the well fades back while a file is dragged over it, so the drop
// target is the only live thing on screen.
export const Dimmed = styled.div<{ $dim: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  opacity: ${(p) => (p.$dim ? DRAG_DIM_OPACITY : 1)};
  transition: opacity ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE};
`;

// --- What is being replaced --------------------------------------------------
// The logo currently in this slot, its name, and where it is used — so the owner sees
// what they are about to change before picking anything. The size brief lives with the
// other file requirements, below, rather than being called out here in accent colour.
export const Target = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceFaint};
`;

// The plate is animated, not swapped: it is chosen by measuring the artwork, so it settles
// a beat after the frame is on screen (and again if the artwork changes). Travelling there
// reads as the backing adapting to the logo; snapping reads as a glitch.
export const TargetFrame = styled.div<{ $frame: 'light' | 'dark' }>`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 120px;
  height: 84px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid ${CASINO_SETTINGS.previewBorder};
  background: ${(p) => (p.$frame === 'dark' ? CASINO_SETTINGS.previewDark : CASINO_SETTINGS.previewLight)};
  overflow: hidden;
  transition: background-color ${ANIM.LOGO_SWAP_MS}ms ease;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const TargetMeta = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

// The slot's name and a "Current" badge on one line: the artwork beside it is what the
// site is using right now, which is the thing being replaced.
export const TargetHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const CurrentBadge = styled(Text)`
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceMuted};
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
`;

export const TargetLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.text};
`;

export const TargetDesc = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

// --- File picker -------------------------------------------------------------
// The well, the chosen-file preview and the in-flight spinner are three faces of the SAME
// slot, so they all stand exactly this tall. Nothing in the dialog moves when one gives
// way to another — arming a drag over a file already picked, dropping it, cancelling, or
// swapping the file for another one. A height to animate would mean the box had moved;
// this way it never does.
const MEDIA_SLOT = css`
  min-height: 156px;
`;
// Also a drop target: the whole well accepts a dragged image, and lights up while one is
// over it. Its children are pointer-transparent so dragging ACROSS them doesn't fire a
// dragleave on the well itself (which would flicker the highlight off and on).
export const DropZone = styled.button<{ $dragging: boolean }>`
  ${MEDIA_SLOT}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 26px 20px;
  text-align: center;
  cursor: pointer;
  border-radius: 14px;
  border: 1.5px dashed ${(p) => (p.$dragging ? CASINO_SETTINGS.cardSelectedBorder : AFFILIATE_FORM.fieldBorder)};
  background: ${(p) => (p.$dragging ? COLORS.surfaceInset : AFFILIATE_FORM.fieldBg)};
  color: ${(p) => (p.$dragging ? CASINO_SETTINGS.cardIconSelected : COLORS.icon)};
  transition:
    border-color ${ANIM.INTERACT_MS}ms,
    background ${ANIM.INTERACT_MS}ms,
    color ${ANIM.INTERACT_MS}ms;

  &:hover {
    border-color: ${CASINO_SETTINGS.cardSelectedBorder};
    background: ${COLORS.surfaceInset};
  }

  & > * {
    pointer-events: none;
  }
`;

// --- Drag-over illustration --------------------------------------------------
// A small stack of sheets with one settling onto it, looping: it reads as "let go and it
// lands here" in a way a static upload arrow can't. Pure DOM (no SVG) so every colour is
// a token and the motion is plain CSS.
// Taller than the stack itself: the dropped sheet starts above it, and that headroom is
// part of the box so the motion is never clipped.
// Both states of the well live in the same boxes and cross-fade: the well's height is
// therefore IDENTICAL idle and mid-drag, so arming or cancelling a drag can't make it
// jump. Sized to the taller of the two (the illustration).
export const DropStage = styled.div`
  position: relative;
  width: 92px;
  height: 72px;
`;

/** One of the two marks, stacked in the stage; only the active one is visible. */
export const DropStageLayer = styled.div<{ $on: boolean }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transform: scale(${(p) => (p.$on ? 1 : 0.9)});
  transition:
    opacity ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE},
    transform ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE};
`;

/** Same treatment for the two captions, so the line never reflows mid-swap. */
export const DropLabels = styled.div`
  position: relative;
  align-self: stretch;
  height: 20px;
`;

export const DropLabelLayer = styled.div<{ $on: boolean }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transform: translateY(${(p) => (p.$on ? '0' : '4px')});
  transition:
    opacity ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE},
    transform ${ANIM.LOGO_DROP_DIM_MS}ms ${ANIM.EASE};
`;

export const DropArt = styled.div`
  position: relative;
  width: 92px;
  height: 72px;
`;

// One sheet of the stack, with a folded top-right corner so it reads as a file rather
// than a plain card.
const sheet = css`
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 38px;
  height: 48px;
  margin-left: -19px;
  border-radius: 6px;
  border: 1.5px solid ${CASINO_SETTINGS.cardSelectedBorder};
  background: ${COLORS.surfaceInset};

  &::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -1px;
    width: 12px;
    height: 12px;
    border-top-right-radius: 6px;
    background: ${CASINO_SETTINGS.cardSelectedBorder};
    clip-path: polygon(0 0, 100% 100%, 100% 0);
  }
`;

/** The two settled sheets: fanned out and holding still — they are the stack. */
export const DropSheetBack = styled.span<{ $side: 'left' | 'right' }>`
  ${sheet}
  height: 44px;
  transform: ${(p) => (p.$side === 'left' ? 'translateX(-21px) rotate(-15deg)' : 'translateX(21px) rotate(15deg)')};
  opacity: 0.5;
`;

// Gravity, then a settle: the sheet accelerates down (ease-in), rebounds twice with the
// bounce decaying, rests a beat, then is lifted smoothly back up to start again. Per-step
// easing is what keeps it from reading as a mechanical up-down. It never goes transparent
// — it is a sheet of paper, not a ghost.
const dropIn = keyframes`
  0% {
    transform: translateY(-24px) rotate(-6deg);
    animation-timing-function: cubic-bezier(0.55, 0, 0.85, 0.4);
  }
  34% {
    transform: translateY(0) rotate(0deg);
    animation-timing-function: cubic-bezier(0.2, 0.9, 0.3, 1);
  }
  46% {
    transform: translateY(-9px) rotate(1.5deg);
    animation-timing-function: cubic-bezier(0.55, 0, 0.85, 0.4);
  }
  56% {
    transform: translateY(0) rotate(0deg);
    animation-timing-function: cubic-bezier(0.2, 0.9, 0.3, 1);
  }
  64% {
    transform: translateY(-3px) rotate(0.5deg);
    animation-timing-function: cubic-bezier(0.55, 0, 0.85, 0.4);
  }
  70%,
  80% {
    transform: translateY(0) rotate(0deg);
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }
  100% {
    transform: translateY(-24px) rotate(-6deg);
  }
`;

/** The sheet being dropped: falls onto the stack, settles, and comes round again. */
export const DropSheetFront = styled.span<{ $running: boolean }>`
  ${sheet}
  border-color: ${CASINO_SETTINGS.cardIconSelected};
  background: ${COLORS.surfaceCard};
  animation: ${dropIn} ${ANIM.LOGO_DROP_LOOP_MS}ms ${ANIM.EASE} infinite;
  animation-play-state: ${(p) => (p.$running ? 'running' : 'paused')};

  &::after {
    background: ${CASINO_SETTINGS.cardIconSelected};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const DropTitle = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.text};
`;

// --- Chosen-file preview -----------------------------------------------------
export const Preview = styled.div`
  ${MEDIA_SLOT}
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceFaint};
`;

// Same animated plate as TargetFrame above — and it matters more here: this frame's tone is
// decided by measuring a file the owner picked a moment ago, so it is always a late answer.
export const PreviewFrame = styled.div<{ $frame: 'light' | 'dark' }>`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 120px;
  height: 84px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid ${CASINO_SETTINGS.previewBorder};
  background: ${(p) => (p.$frame === 'dark' ? CASINO_SETTINGS.previewDark : CASINO_SETTINGS.previewLight)};
  overflow: hidden;
  transition: background-color ${ANIM.LOGO_SWAP_MS}ms ease;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

// Contain the artwork whatever its size or shape: cap both axes to the (definite) frame's
// content box AND force the grid item's auto-minimum to 0 — without that a bare <img>
// keeps its intrinsic minimum size, so a full-resolution logo (an owner's upload above
// all) overflows the plate and is cropped by `overflow: hidden`, misrepresenting its
// shape. `object-fit: contain` then letterboxes it at its true aspect ratio.
//
// $fade = the picked file, which appears once it has decoded rather than snapping into a
// frame that is at the same time settling on its plate. Lit by the component on load.
export const PreviewImg = styled.img<{ $fade?: boolean; $shown?: boolean }>`
  display: block;
  max-width: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  object-fit: contain;
  opacity: ${(p) => (p.$fade && !p.$shown ? 0 : 1)};
  transition: opacity ${ANIM.LOGO_SWAP_MS}ms ease;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const PreviewMeta = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FileName = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FileSize = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  color: ${COLORS.textMuted};
`;

export const Relink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: ${CASINO_SETTINGS.cardIconSelected};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

// --- What Lumio should do with the upload ------------------------------------
// One row per job (clean the file up; rebuild the rest of the set from it): the copy on
// the left, its switch on the right. Bordered rows rather than bare switches, so they
// read as decisions about the upload and not as page settings.
// ONE card holding every job, not a card each: they are two settings on the same upload,
// so a shared surface with a hairline between them reads as one decision point rather
// than two competing ones.
export const Options = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceFaint};
`;

export const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;

  & + & {
    border-top: 1px solid ${COLORS.hairline};
  }
`;

// The switch sits at its natural 48×24: as a bare flex item next to a wide block of copy
// it gets shrunk, which squashes the track while the knob keeps its size and overhangs.
export const OptionSwitch = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

export const OptionText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

// The Lumio mark leads the option's title, so it reads at a glance as something the
// assistant does rather than a plain upload setting. Same eyebrow treatment the Lumio
// panel header and the customizer's suggestion headers use.
export const OptionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

// A multicolour gradient asset, so it is a plain <img>, not an Icon glyph. It drains to
// grey when its switch is off: Lumio isn't doing that job, so its mark shouldn't sit there
// in full colour claiming otherwise.
export const OptionMark = styled.img<{ $muted: boolean }>`
  flex: 0 0 auto;
  display: block;
  width: 16px;
  height: 16px;
  filter: ${(p) => (p.$muted ? 'grayscale(1)' : 'none')};
  opacity: ${(p) => (p.$muted ? 0.55 : 1)};
  /* Springs to life when switched on and settles back when switched off: the overshoot
   * curve carries the scale past its target and returns it, so the mark reads as
   * something waking up rather than a value sliding between two numbers. A transition
   * rather than a keyframe animation, so it fires on the FLIP and never on open.
   * SCALE ONLY — this is the Lumio brand mark, so it is never rotated, skewed or
   * otherwise redrawn; it only gets smaller and loses its colour. */
  transform: ${(p) => (p.$muted ? 'scale(0.78)' : 'scale(1)')};
  transition:
    transform ${ANIM.LOGO_MARK_SPRING_MS}ms ${ANIM.EASE_SPRING},
    filter ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  @media (prefers-reduced-motion: reduce) {
    transition:
      filter ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
      opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
    transform: none;
  }
`;

export const OptionLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const OptionHint = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

// The second paragraph of an option's hint (what turning it OFF does), set off by a blank
// line so it reads as an aside rather than more of the same sentence.
export const OptionAside = styled(OptionHint)`
  margin-top: 10px;
`;

// --- Guidelines + scope note -------------------------------------------------
export const Guidelines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Guideline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  /* The glyph keeps its size on every viewport: as a flex item next to a long line of
     copy it would otherwise be squeezed to a sliver on a phone. */
  & > svg {
    flex: 0 0 auto;
  }
`;

export const GuidelineText = styled(Text)`
  font-size: 13px;
  font-weight: 300;
  color: ${COLORS.textMuted};
`;

export const InfoBox = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${CASINO_SETTINGS.lockBorder};
  background: ${CASINO_SETTINGS.lockBg};

  & > svg {
    flex: 0 0 auto;
  }
`;

export const InfoText = styled(Text)`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: ${COLORS.text};
`;

// --- In-flight processing (decode + resize to the slot's canonical size) ------
const spin = keyframes`
  to {
    transform: rotate(1turn);
  }
`;

export const Processing = styled.div`
  ${MEDIA_SLOT}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 30px 20px;
  text-align: center;
`;

/** Ring spinner: accent arc over a faint track. */
export const Spinner = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid ${COLORS.panelBorder};
  border-top-color: ${COLORS.accent};
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ProcessingText = styled(Text)`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

// --- Checks on the picked file -----------------------------------------------
// A mismatch never blocks the replacement (an owner may knowingly supply a differently
// proportioned mark), so these are amber advisories, not the red error row.
export const WarnRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  & > svg {
    flex: 0 0 auto;
  }
`;

export const WarnText = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: ${CASINO_SETTINGS.lockFg};
`;

// --- Error row ---------------------------------------------------------------
export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & > svg {
    flex: 0 0 auto;
  }
`;

export const ErrorText = styled(Text)`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: ${COLORS.statusDanger};
`;
